import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardCheck, ChevronDown, ChevronRight, BookOpen, Wrench } from "lucide-react";
import { guideLink } from "../../lib/standardsApi";
import { Pill, num, scoreTone } from "./Layer";
import { cn } from "../../lib/utils";

const SEV = { high: ["High", "critical"], medium: ["Medium", "warning"], low: ["Low", "info"], info: ["Note", "neutral"] };

function Rows({ rows }) {
  if (!rows.length) return null;
  const cols = [...new Set(rows.flatMap(r => Object.keys(r.cells)))];
  return (
    <div className="overflow-x-auto border border-border rounded-sm">
      <table className="text-[11.5px] font-mono-tabular w-full"><thead><tr><th className="text-left px-2 py-1 border-b border-border text-muted w-14">Row</th>
        {cols.map((c, i) => <th key={c} className={cn("text-left px-2 py-1 border-b border-border whitespace-nowrap", i === 0 ? "text-primary" : "text-muted")}>{c}</th>)}</tr></thead>
        <tbody>{rows.map(r => <tr key={r.row} className="border-b border-border/50"><td className="px-2 py-1 text-muted">{num(r.row + 1)}</td>
          {cols.map((c, i) => <td key={c} className={cn("px-2 py-1 whitespace-pre max-w-[180px] truncate", i === 0 && "bg-warning/10")}>{r.cells[c] === "" ? <span className="text-muted/60 italic">blank</span> : r.cells[c]}</td>)}</tr>)}</tbody></table>
    </div>
  );
}

function Values({ x }) {
  if (!x.values.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={`rv-std-values-${x.id}`}>
      {x.values.map((v, i) => <span key={i} className="text-[11px] font-mono-tabular px-1.5 py-0.5 border border-border rounded-sm whitespace-pre">
        {"value" in v ? <>{v.value === "" ? "blank" : v.value}<span className="text-muted"> · {num(v.rows)}</span></>
          : "standard" in v ? <>{v.column} <span className="text-muted">=</span> {v.standard}</>
          : <>{v.column}{v.closest ? <span className="text-muted"> → {v.closest}?</span> : null}{v.personal ? <span className="text-warning"> · personal</span> : null}</>}</span>)}
    </div>
  );
}

function Flag({ x, canGuide }) {
  const [open, setOpen] = useState(false);
  const [label, tone] = SEV[x.severity];
  return (
    <div className="border-b border-border/60 last:border-0" data-testid={`rv-std-flag-${x.id}`}>
      <button data-testid={`rv-std-flag-${x.id}-toggle`} onClick={() => setOpen(o => !o)} className="w-full text-left px-3 py-2 flex items-start gap-2 hover:bg-surface-2/50 transition-colors">
        {open ? <ChevronDown size={13} className="mt-0.5 text-muted shrink-0" /> : <ChevronRight size={13} className="mt-0.5 text-muted shrink-0" />}
        <Pill tone={tone}>{label}</Pill>
        <span className="flex-1 text-[12.5px] leading-snug">{x.title}</span>
        {x.count != null && <span className="text-[11px] font-mono-tabular text-muted whitespace-nowrap" data-testid={`rv-std-flag-${x.id}-count`}>{num(x.count)} {x.count === 1 ? "row" : "rows"}</span>}
      </button>
      {open && <div className="px-3 pb-3 pl-9 space-y-2 text-[12px]" data-testid={`rv-std-flag-${x.id}-detail`}>
        <div className="text-muted leading-snug">{x.detail}</div>
        {x.fix && <div className="flex gap-1.5 text-success"><Wrench size={12} className="mt-0.5 shrink-0" />{x.fix}</div>}
        <Values x={x} />
        <Rows rows={x.rows} />
        {canGuide && x.guide && <Link data-testid={`rv-std-flag-${x.id}-guide`} to={guideLink(x.guide.table, x.guide.column)} className="inline-flex items-center gap-1.5 text-primary hover:underline">
          <BookOpen size={12} />{x.guide.column ? `${x.guide.column} in the standard` : "Open the standard"}</Link>}
      </div>}
    </div>
  );
}

// Bronze review: how far the file is from its table's data standard. Warnings only — the blocking problems still decide promotion.
export default function StandardsCheck({ s, canGuide }) {
  if (!s) return null;
  const parts = s.parts ? [["Columns", s.parts.columns], ["Values", s.parts.values], ["Codes in masters", s.parts.references]] : [];
  return (
    <section className="border border-border rounded-sm" data-testid="rv-standards">
      <div className="px-3 py-2.5 border-b border-border flex flex-wrap items-center gap-2">
        <ClipboardCheck size={14} className="text-primary" />
        <div className="flex-1 min-w-0"><div className="font-semibold text-[13px]">Standards check{s.label ? ` · ${s.label}` : ""}</div>
          <div className="text-[11px] text-muted">Warnings only — they don't block promotion.{s.sampled ? ` From the ${num(s.rows_checked)}-row review sample.` : ""}</div></div>
        {s.score != null && <div className="text-right"><div className={cn("font-mono-tabular text-xl font-semibold leading-none", scoreTone(s.score))} data-testid="rv-std-score">{s.score}</div>
          <div className="text-[10px] text-muted uppercase tracking-widest mt-0.5">conformance</div></div>}
        {canGuide && s.table && <Link data-testid="rv-std-guide" to={guideLink(s.table)} className="text-[11.5px] text-primary hover:underline inline-flex items-center gap-1"><BookOpen size={12} />Standard</Link>}
      </div>
      {parts.length > 0 && <div className="px-3 py-2 border-b border-border flex flex-wrap gap-x-5 gap-y-1 text-[11.5px]" data-testid="rv-std-parts">
        {parts.map(([k, v]) => <span key={k} className="text-muted">{k} <span className={cn("font-mono-tabular font-semibold", scoreTone(v))}>{v == null ? "not checked" : v}</span></span>)}</div>}
      {s.flags.length ? <div className="max-h-[52vh] overflow-y-auto" data-testid="rv-std-flags">{s.flags.map(x => <Flag key={x.id} x={x} canGuide={canGuide} />)}</div>
        : <div className="px-3 py-4 text-[12px] text-success" data-testid="rv-std-clean">The file follows its standard — nothing to flag.</div>}
    </section>
  );
}
